'use client';
import { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, MessageCircle, CheckCircle, Smartphone, Send, Link2 } from 'lucide-react';
import { useT, useI18n } from '../i18n';
import { sendTrack, getRef } from '../lib/track';

const CF_WORKER_URL = 'https://jkd-whatsapp-verification.jimsbond007.workers.dev';

type Step = 'form' | 'verify' | 'success';

export default function DiagnosticPage() {
  const t = useT();
  const { locale } = useI18n();
  const [step, setStep] = useState<Step>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationData, setVerificationData] = useState<{
    token: string;
    deepLink: string;
    message: string;
  } | null>(null);

  useEffect(() => {
    if (step !== 'verify' || !verificationData) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${CF_WORKER_URL}/debug/token/${verificationData.token}`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.found && data.data?.verified) {
          setStep('success');
        }
      } catch (e) {
        // ignore polling errors
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [step, verificationData]);

  const [copied, setCopied] = useState(false);

  const REFERRAL_URL = 'https://jkdcoding.com/gf/diag';
  const referralText = `${t('share.prefill')} ${REFERRAL_URL}`;
  const shareWhatsAppUrl = `https://wa.me/?text=${encodeURIComponent(referralText)}`;

  const handleCopyReferral = async () => {
    try {
      await navigator.clipboard.writeText(referralText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      // clipboard API 不可用時靜默
    }
  };

  const handleShareWhatsApp = () => {
    sendTrack({ event: 'whatsapp_click', page: '/diagnosis-share' });
  };

  const handleWebShare = async () => {
    try {
      await navigator.share({ title: 'JKD Coding', text: referralText, url: REFERRAL_URL });
    } catch (e) {
      // 用戶取消或 API 不可用時靜默
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    role: '',
    email: '',
    businessType: '',
    challenge: '',
    goals: '',
    budget: '',
  });

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const message = `
${t('diag.msgOrg')}：${formData.organization}
${t('diag.msgRole')}：${formData.role}
${t('diag.msgChallenge')}：${formData.challenge}
${t('diag.msgGoals')}：${formData.goals}
${t('diag.msgBudget')}：${formData.budget || t('diag.msgUnfilled')}
`.trim();

    try {
      const response = await fetch(`${CF_WORKER_URL}/submit-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          businessType: formData.businessType,
          message,
          locale,
          ref: getRef(),
        }),
      });

      if (!response.ok) throw new Error('Submit failed');
      const data = await response.json();

      if (data.success) {
        sendTrack({ event: 'diagnosis_submit' });
        setVerificationData({
          token: data.token,
          deepLink: data.deepLink,
          message: data.message,
        });
        setStep('verify');
      }
    } catch (error) {
      console.error(error);
      alert(t('diag.alertFail'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 'verify' && verificationData) {
    return (
      <div className="min-h-screen bg-jkd-black text-white flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <button
            onClick={() => (window.location.href = '/')}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-jkd-gold transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('diag.back')}
          </button>
          <div className="card-dark text-center">
            <div className="w-16 h-16 bg-jkd-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Smartphone className="w-8 h-8 text-jkd-gold" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">{t('diag.verifyTitle')}</h1>
            <p className="text-gray-400 mb-6">{t('diag.verifyDesc')}</p>
            <div className="bg-jkd-black border border-jkd-gray-200 rounded-lg p-4 mb-6">
              <p className="text-gray-500 text-xs mb-2">{t('diag.code')}</p>
              <p className="text-2xl font-mono font-bold text-jkd-gold tracking-wider">{verificationData.token}</p>
            </div>
            <a
              href={verificationData.deepLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full btn-gold flex items-center justify-center gap-2 mb-4"
            >
              <MessageCircle className="w-5 h-5" />
              {t('diag.openWhatsApp')}
            </a>
            <p className="text-gray-500 text-xs mb-6">{t('diag.prefilled')}</p>
            <button onClick={() => setStep('form')} className="text-gray-400 hover:text-white text-sm transition-colors">
              ← {t('diag.goBack')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-jkd-black text-white flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <div className="text-center card-dark">
            <CheckCircle className="w-16 h-16 text-jkd-gold mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-3">{t('diag.successTitle')}</h1>
            <p className="text-gray-400">{t('diag.successDesc')}</p>
          </div>

          {/* 蒸餾所交叉銷售（P1 接駁）：診斷之後的下一步 */}
          <div className="mt-6 card-dark card-gold-border p-6">
            <h2 className="text-lg font-bold text-white mb-2">{t('distill.xs.title')}</h2>
            <p className="text-gray-400 text-sm mb-4">{t('distill.xs.body')}</p>
            <a href="/distill/" className="inline-flex items-center gap-2 text-jkd-gold font-bold text-sm hover:text-white transition-colors">
              {t('distill.xs.cta')} <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* P0 裂變入口：推薦給一位朋友（中性稱呼，不顯示推薦人身份） */}
          <div className="mt-6 card-dark card-gold-border p-6">
            <h2 className="text-lg font-bold text-white mb-2">{t('share.title')}</h2>
            <p className="text-gray-400 text-sm mb-4">{t('share.desc')}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCopyReferral}
                className="flex-1 btn-outline text-sm py-3 inline-flex items-center justify-center gap-2"
              >
                {copied ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Link2 className="w-4 h-4" />}
                {copied ? t('share.copied') : t('share.copy')}
              </button>
              <a
                onClick={handleShareWhatsApp}
                href={shareWhatsAppUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 btn-gold text-sm py-3 inline-flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                {t('share.whatsapp')}
              </a>
            </div>
            {'share' in navigator && (
              <button onClick={handleWebShare} className="mt-3 w-full text-gray-400 hover:text-jkd-gold text-xs transition-colors">
                {t('share.more')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-jkd-black text-white py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => (window.location.href = '/')}
          className="inline-flex items-center gap-2 text-gray-400 hover:text-jkd-gold transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          {t('diag.back')}
        </button>

        <div className="text-center mb-10">
          <span className="inline-block px-4 py-2 mb-4 border border-jkd-gold/30 rounded-full text-jkd-gold text-sm tracking-widest uppercase">
            {t('diag.tag')}
          </span>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-4">{t('diag.title')}</h1>
          <p className="text-lg text-gray-300">{t('diag.subtitle')}</p>
        </div>

        <div className="card-dark card-gold-border">
          <h2 className="text-xl font-bold text-white mb-2">{t('diag.step1Title')}</h2>
          <p className="text-gray-400 text-sm mb-6">{t('diag.step1Desc')}</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-gray-300 text-sm mb-1.5">{t('diag.name')}</label>
                <input
                  required
                  type="text"
                  className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white placeholder-gray-500 focus:border-jkd-gold focus:outline-none transition-colors"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1.5">{t('diag.email')}</label>
                <input
                  required
                  type="email"
                  className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white placeholder-gray-500 focus:border-jkd-gold focus:outline-none transition-colors"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-gray-300 text-sm mb-1.5">{t('diag.organization')}</label>
                <input
                  required
                  type="text"
                  className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white placeholder-gray-500 focus:border-jkd-gold focus:outline-none transition-colors"
                  value={formData.organization}
                  onChange={(e) => handleChange('organization', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1.5">{t('diag.role')}</label>
                <input
                  required
                  type="text"
                  className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white placeholder-gray-500 focus:border-jkd-gold focus:outline-none transition-colors"
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-gray-300 text-sm mb-1.5">{t('diag.businessType')}</label>
                <select
                  required
                  className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white focus:border-jkd-gold focus:outline-none transition-colors"
                  value={formData.businessType}
                  onChange={(e) => handleChange('businessType', e.target.value)}
                >
                  <option value="">{t('diag.businessPlaceholder')}</option>
                  <option value="ngo">{t('diag.ngo')}</option>
                  <option value="sme">{t('diag.sme')}</option>
                  <option value="personal">{t('diag.personal')}</option>
                  <option value="other">{t('diag.other')}</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1.5">{t('diag.challenge')}</label>
                <select
                  required
                  className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white focus:border-jkd-gold focus:outline-none transition-colors"
                  value={formData.challenge}
                  onChange={(e) => handleChange('challenge', e.target.value)}
                >
                  <option value="">{t('diag.challengePlaceholder')}</option>
                  <option value="conversion">{t('diag.challenge1')}</option>
                  <option value="followup">{t('diag.challenge2')}</option>
                  <option value="risk">{t('diag.challenge3')}</option>
                  <option value="adoption">{t('diag.challenge4')}</option>
                  <option value="content">{t('diag.challenge5')}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-gray-300 text-sm mb-1.5">{t('diag.goals')}</label>
              <textarea
                required
                rows={4}
                className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white placeholder-gray-500 focus:border-jkd-gold focus:outline-none transition-colors resize-none"
                placeholder={t('diag.goalsPlaceholder')}
                value={formData.goals}
                onChange={(e) => handleChange('goals', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-gray-300 text-sm mb-1.5">{t('diag.budget')}</label>
              <select
                className="w-full px-4 py-2.5 bg-jkd-black border border-jkd-gray-200 rounded text-white focus:border-jkd-gold focus:outline-none transition-colors"
                value={formData.budget}
                onChange={(e) => handleChange('budget', e.target.value)}
              >
                <option value="">{t('diag.budgetPlaceholder')}</option>
                <option value="8k-20k">{t('diag.budget1')}</option>
                <option value="20k-50k">{t('diag.budget2')}</option>
                <option value="50k-150k">{t('diag.budget3')}</option>
                <option value="150k+">{t('diag.budget4')}</option>
              </select>
            </div>

            <div className="bg-jkd-black/50 border border-jkd-gold/20 rounded-lg p-3">
              <p className="text-gray-400 text-xs">{t('diag.privacy')}</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-gold flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {isSubmitting ? t('diag.submitting') : t('diag.submit')}
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
